import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Users,
  FileText,
  Bot,
  BarChart3,
  Calendar,
  Euro,
  Sparkles,
  Palette,
  Gift,
  MessageSquare,
  Layers,
  Shield,
  FileDown,
  Menu,
  X,
} from 'lucide-react';

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  category?: string;
}

export const menuItems: MenuItem[] = [
  // Website
  { id: 'leads', label: 'Kontaktanfragen', icon: Users, category: 'Website' },
  { id: 'sections', label: 'Sektionen', icon: Layers, category: 'Website' },
  { id: 'blog', label: 'Blog-Verwaltung', icon: FileText, category: 'Website' },
  { id: 'themes', label: 'Themes', icon: Palette, category: 'Website' },
  // Chatbot
  { id: 'bot', label: 'Bot-Wissen', icon: Bot, category: 'Chatbot' },
  { id: 'analytics', label: 'Chat-Statistik', icon: BarChart3, category: 'Chatbot' },
  { id: 'chatbot-admin', label: 'Chatbot-Kunden', icon: MessageSquare, category: 'Chatbot' },
  // Services
  { id: 'appointments', label: 'Termine', icon: Calendar, category: 'Services' },
  { id: 'prices', label: 'Preise', icon: Euro, category: 'Services' },
  { id: 'vouchers', label: 'Gutscheine', icon: Gift, category: 'Services' },
  { id: 'offers', label: 'Angebote', icon: FileDown, category: 'Services' },
  // Produkte
  { id: 'kompakt', label: 'Kompakt', icon: Sparkles, category: 'Produkte' },
  { id: 'klartext-check', label: 'Klartext-Check', icon: Shield, category: 'Produkte' },
];

interface DashboardSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DashboardSidebar({ activeTab, onTabChange, isOpen, onOpenChange }: DashboardSidebarProps) {
  const handleItemClick = (id: string) => {
    onTabChange(id);
    onOpenChange(false);
  };

  const renderMenuItems = () => {
    const categories = [...new Set(menuItems.map(item => item.category))];
    
    return (
      <div className="space-y-6 py-4">
        {categories.map(category => (
          <div key={category}>
            <h3 className="mb-2 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              {category}
            </h3>
            <div className="space-y-1">
              {menuItems
                .filter(item => item.category === category)
                .map(item => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item.id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700 border-l-4 border-cyan-600'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      )}
                    >
                      <Icon className={cn('w-5 h-5', isActive ? 'text-cyan-600' : 'text-slate-400')} />
                      {item.label}
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Menu className="w-5 h-5" />
          <span className="hidden sm:inline">Menü</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="p-4 border-b bg-gradient-to-r from-cyan-600 to-teal-600">
          <SheetTitle className="text-white flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Dashboard Navigation
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-80px)]">
          {renderMenuItems()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// Compact menu indicator for showing current section
export function CurrentSectionBadge({ activeTab }: { activeTab: string }) {
  const currentItem = menuItems.find(item => item.id === activeTab);
  if (!currentItem) return null;
  
  const Icon = currentItem.icon;
  
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-lg text-sm font-medium">
      <Icon className="w-4 h-4" />
      {currentItem.label}
    </div>
  );
}
